import cx from "classnames";
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDownIcon,
  Chip,
  ExternalLinkIcon,
  TrashIcon,
} from "@gataca/design-system/web";
import styles from "./badgesTable.module.scss";
import {
  CellContentTypes,
  gaColors,
  TextAlignTypes,
  TooltipArrowPosition,
  TooltipPosition,
} from "../../../../assets/globalConstants";
import {
  IBadgesTableData,
  PaginationProps,
} from "../../../../types/interfaces";
import LightPanel from "../../../../components/panels/LightPanel/LightPanel";
import { GaPagination } from "../../../../components/Pagination/Pagination";
import CopyCell from "../../../../components/table/cells/copyCell/CopyCell";
import { centerEllipsisTrim } from "../../../../utils/formatDataUtil";
import moment from "moment";
import GaHoverTooltip from "../../../../components/elements/tooltips/GaHoverTooltip/GaHoverTooltip";
import { useTranslation } from "react-i18next";
// import LanguageSelector from "../../../../components/languageSelector/LanguageSelector";

export type BadgesTableProps = {
  tableData?: IBadgesTableData[];
  headerData: {
    key: string;
    text: string;
    textAlign?: TextAlignTypes;
    type?: CellContentTypes;
    tag?: boolean;
    short?: boolean;
    tagText?: string;
    action?: (x?) => void;
  }[];
  canDelete?: boolean;
  noResults?: boolean;
  paginationData?: PaginationProps;
  deleteItem?: (x?) => void;
  searchValue: string;
  searchValueMessage: boolean;
  resultsOnLoad: boolean;
  searchValueOnEnter: string;
};

const BadgesTable: React.FC<BadgesTableProps> = (props) => {
  const {
    tableData,
    headerData,
    paginationData,
    noResults,
    canDelete,
    deleteItem,
    searchValue,
    searchValueMessage,
    resultsOnLoad,
    searchValueOnEnter,
  } = props;

  const { t } = useTranslation();

  useEffect(() => {}, [tableData]);

  const showPagination =
    !!paginationData &&
    paginationData?.totalItems &&
    paginationData.pageSize &&
    paginationData?.totalItems > paginationData.pageSize &&
    !noResults;

  return (
    <div className={styles.container}>
      {/* <LanguageSelector /> */}
      {noResults && searchValueOnEnter && searchValue ? (
        <p className={cx("marginBottom24 bodyRegularMD neutral700")}>
          {t("badgesList.noResults1")}
          <span className={cx("bodyBoldMD neutral700")}>
            {" "}
            "{searchValueOnEnter}"{" "}
          </span>
          {t("badgesList.noResults2")}
        </p>
      ) : searchValueMessage &&
        searchValueOnEnter &&
        searchValue &&
        !noResults ? (
        <p className={cx("marginBottom24 bodyRegularMD neutral700")}>
          {paginationData?.totalItems}
          {t("badgesList.results")}
          <span className={cx("bodyBoldMD neutral700")}>
            {" "}
            "{searchValueOnEnter}"{" "}
          </span>
        </p>
      ) : !resultsOnLoad ? (
        <p className={cx("marginBottom24 bodyRegularMD neutral700")}>
          {t("badgesList.notAvailableCertifications")}
        </p>
      ) : null}

      {resultsOnLoad ? (
        <LightPanel className={styles.panelContainer}>
          <div className={cx(styles.tableContainer)}>
            <table>
              <thead>
                <tr>
                  {headerData.map((header, idx) => (
                    <th
                      className={cx(
                        "bodyRegularSM neutral700",
                        noResults ? styles.noBorder : ""
                      )}
                      key={header.key + idx + Math.random()}>
                      <div className={cx("width100")}>
                        {header.text ? (
                          <span className={cx("bodyRegularSM neutral700")}>
                            {t(header.text)}
                          </span>
                        ) : null}
                        {idx !== headerData?.length - 1 ? (
                          <ChevronDownIcon color={"grey"} size={"16"} />
                        ) : null}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!!tableData
                  ? tableData.map((item, idx) => {
                      const itemDeleted = item?.data?.deletedAt?.length;
                      return (
                        <tr
                          data-id={idx}
                          key={idx + Math.random()}
                          className={
                            idx === tableData?.length - 1 ? styles.noBorder : ""
                          }>
                          {!!item ? (
                            <>
                              <CopyCell
                                item={item}
                                textToCopy={item?.id}
                                textToCopyDisplayValue={centerEllipsisTrim(
                                  item?.id,
                                  20,
                                  "..."
                                )}
                                mainDataClickable
                              />
                              <td className={styles.recipient}>
                                <span className={cx("buttonMD neutral1000")}>
                                  {item?.data?.campaign}
                                </span>
                              </td>
                              <td className={styles.creationDate}>
                                <p className={cx("buttonMD neutral1000")}>
                                  {item?.data?.createdAt
                                    ? moment(item?.data?.createdAt)?.format(
                                        "MMMM DD, YYYY"
                                      )
                                    : ""}
                                </p>
                              </td>
                              <td className={styles.recipient}>
                                <span className={cx("buttonMD neutral1000")}>
                                  {item?.data?.email}
                                </span>
                              </td>

                              <td className={styles.status}>
                                <Chip
                                  color={itemDeleted ? "grey" : "green"}
                                  className="widthFitContent"
                                  chipSize="large"
                                  text={
                                    itemDeleted
                                      ? t("badgesList.deleted")
                                      : t("badgesList.created")
                                  }
                                />
                              </td>

                              <td
                                className={`${styles?.noBorder} ${styles?.tableActions}`}>
                                {canDelete ? (
                                  !itemDeleted ? (
                                    <div
                                      className={styles.actionButton}
                                      onClick={() =>
                                        !itemDeleted && deleteItem
                                          ? deleteItem(item?.id)
                                          : {}
                                      }>
                                      <GaHoverTooltip
                                        info={t("public.delete")}
                                        label={
                                          <TrashIcon
                                            color={gaColors.alert300}
                                            size={"24"}
                                          />
                                        }
                                        position={TooltipPosition.top}
                                        positionArrowDirection={
                                          TooltipArrowPosition.center
                                        }
                                      />
                                    </div>
                                  ) : (
                                    <TrashIcon
                                      color={gaColors.neutral400}
                                      size={"24"}
                                    />
                                  )
                                ) : null}
                                {!itemDeleted ? (
                                  <Link to={item?.data?.url} target={"_blank"}>
                                    <ExternalLinkIcon color="black" size="24" />
                                  </Link>
                                ) : (
                                  <ExternalLinkIcon
                                    color={gaColors.neutral400}
                                    size="24"
                                  />
                                )}
                              </td>
                            </>
                          ) : null}
                        </tr>
                      );
                    })
                  : null}
              </tbody>
            </table>
          </div>
        </LightPanel>
      ) : null}

      {paginationData && showPagination ? (
        <div>
          <GaPagination {...paginationData} />
        </div>
      ) : null}
    </div>
  );
};

export default BadgesTable;
